import { Inject } from '@odinjs/odin';
import { FieldAPI, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableCloseEvent, AbortableSubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters } from '../../../../utils/parameters';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../constants/routes';
import BaseController from '../../../BaseController';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { MedicationViewerParams } from '../medication-viewer/medication-viewer-params';
import { ConsistAdministrationMarginInfo } from '../recommendation-orders/consist-administration-margin-info';
import { PrescriptionType } from '../../services/timeline-service';
import {FragmentNames, GeneratePatientRefusalAliases} from '../../../../../src/codegen';
import { Validator } from '@philips-internal/oswald-ui-core/lib/services/model-service';
import { PatientProfile } from './generate-patient-refusal-types';

import './generate-patient-refusal-controller.css';
import { Value } from '@philips-internal/oswald-ui-core';

@Controller({ component: GeneratePatientRefusalAliases.GENERATE_PATIENT_REFUSAL_LAYOUT, fragment: FragmentNames.GENERATE_PATIENT_REFUSAL })
class GeneratePatientRefusalController extends BaseController {

  @Inject('refusal-medication-name-text')
  refusalMedicationNameText!: TextAPI;

  @Inject(GeneratePatientRefusalAliases.REFUSAL_MEDICATION_JUSTIFICATION_FIELD)
  justificationFieldAPI!: FieldAPI;

  state = {
    userName: foundationContext.getUser()?.username
  }

  showToast(message: string, typeMessage: ToastLevel): void {
    const toastTime = 5000;

    ToastService.open({
      dismissExpression: message,
      dismissible: true,
      highlight: false,
      id: 'generate-patient-refusal',
      level: typeMessage,
      message: message,
      placement: Placement.BOTTOM,
      showIcon: true,
      type: ToastType.BANNER,
    });

    setTimeout(() => {
      ToastService.close('generate-patient-refusal');
    }, toastTime);
  }

  private verifyProfile(): void {
    const tasyFunctionName = 'ADEP#1113';
    const profile = FoundationUtils.findModuleParam(tasyFunctionName, ADEP_E1113Parameters.INFORM_OBS_WHEN_REFUSAL_MEDICATION);
    const validator: Validator = { name: 'required', options: {}};
    if (profile === PatientProfile.MANDATORY ) {
      this.justificationFieldAPI.addValidator(validator);
      return;
    }
    this.justificationFieldAPI.removeValidator('required');
  }

  async patientMedicationRefusalConfirmation(event: AbortableSubmitEvent): Promise<void> {
    const eventDate = moment().format(ISO_FULL_DATE_FORMAT);

    const itemType = MedicationViewerParams.params.itemType ?? PrescriptionType.MEDICINE;

    const validation = await ConsistAdministrationMarginInfo.validate({
      administrationDate: eventDate,
      itemType,
      justification: event.record.textArea ? String(event.record.textArea) : '',
      scheduleSequenceNumber: Number(MedicationViewerParams.params.hourSequence ?? 0),
    });

    if (!validation.allowed) {
      this.showToast(validation.message, ToastLevel.ERROR);
      return;
    }

    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;

    const body = {
      encounterNumber: encounterId,
      eventDate,
      hourSequence: MedicationViewerParams.params.hourSequence,
      itemCode: MedicationViewerParams.params.itemCode,
      justificationDescription: event.record.textArea,
      prescriptionNumber: MedicationViewerParams.params.prescriptionNumber,
      userName: this.state.userName,
    } as Record<string, Value>;

    HttpService.post(Endpoint.ADEP_GENERATE_PATIENT_REFUSAL, {}, { body })
      .then(() => {
        this.showToast(MessageService.translate('text.finish-patient-refusal'), ToastLevel.SUCCESS);
        applicationContext.redirect(redirectRoute, { encounterId, patientId });
        applicationContext.clearNavigationStack();
      }).catch((err) => {
        const displayError = err?.body;
        this.showToast(displayError, ToastLevel.ERROR);
        applicationContext.popNavigationItem();
      });
  }

  @OnMount({ target: GeneratePatientRefusalAliases.GENERATE_PATIENT_REFUSAL_LAYOUT })
  onMount(): void {
    InformationBarUtils.collapsedBar = true;
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, patientId });
  }

  @OnMount({ target: GeneratePatientRefusalAliases.REFUSAL_MEDICATION_JUSTIFICATION_FIELD })
  handleMountField(): void {
    this.verifyProfile();
  }

  @OnChange({ target: GeneratePatientRefusalAliases.REFUSAL_MEDICATION_JUSTIFICATION_FIELD })
  handleChangeField(): void {
    this.verifyProfile();
  }

  @OnMount({ target: 'refusal-medication-name-text' })
  onRefusalMedicationNameTextMount(): void {
    const medicationName = MedicationViewerParams.params.medicationName;
    this.refusalMedicationNameText.setValue(String(medicationName));
  }

  @OnBeforeSubmit({ target: GeneratePatientRefusalAliases.REFUSAL_MEDICATION_FORM })
  onChangeGeneratePatientRefusalForm(event: AbortableSubmitEvent): void {
    this.patientMedicationRefusalConfirmation(event);
  }

  @OnBeforeCancel({ target: GeneratePatientRefusalAliases.REFUSAL_MEDICATION_FORM })
  onCancelPatienteRefusalForm(event: AbortableCloseEvent): void {
    event.abort();
    applicationContext.popNavigationItem();
  }
}

export default GeneratePatientRefusalController;
